import React, { useEffect, useRef, useState } from 'react';
import { Box, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GalleryViewLoader from 'components/common/gallery_view_loader';
import { shopImageSrcSet, shopImageUrl } from 'utilities/images';
import { ReactComponent as CircularArrowIcon } from 'images/icons/circular-arrow.svg';
import { trackCdp } from 'utilities/tracking';
import { MarketPill } from '@market/react';
import { useTranslation } from 'react-i18next';

export const AnimationTypes = {
  None: 'None',
  FullRevolution: 'FullRevolution',
  FrontView: 'FrontView',
};

export const DRAG_TO_SPIN_ID = 'three-sixty-drag-to-spin';
export const CANVAS_ID = 'three-sixty-canvas';
export const SLIDER_INTRO_CONTAINER_ID = 'three-sixty-slider-intro-container';

const TrackingEventType = {
  Click: 'Click',
  Rotate: 'Rotate',
  AnimationComplete: 'AnimationComplete',
  AnimationInterrupted: 'AnimationInterrupted', // Interrupted by rotate
};
const CENTERED_OFFSET_MULTIPLIER = 0.5;

let rotateInterval = null;

const ThreeSixtyView = (props) => {
  const {
    title,
    images = [],
    cart = {},
    product = {},
    initialRotationPercentage = 0,
    movementThreshold = 8,
    lazyLoad = false,
    inView = true,
    introAnimation = AnimationTypes.None,
    fps = 25,
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();

  if (images.length === 0) {
    return null;
  }

  const calculatedInitalRotationIndex =
    Math.floor((Math.abs(initialRotationPercentage) / 100) * images.length) %
    images.length;

  const [currentImageIdx, setCurrentImageIdx] = useState(
    calculatedInitalRotationIndex,
  );
  const [loadedImages, setLoadedImages] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [lastX, setLastX] = useState(null);
  const [isSliderVisible, setIsSliderVisible] = React.useState(false);

  const canvasRef = useRef(null);

  const allImagesLoaded = loadedImages.length === images.length;
  const firstImageLoadedButNotAll =
    !allImagesLoaded &&
    loadedImages[calculatedInitalRotationIndex] !== undefined;
  let rotationCount = 0;

  const trackEvent = (trackingEventType) => {
    if (
      !product ||
      Object.keys(product).length === 0 ||
      !cart ||
      Object.keys(cart).length === 0
    ) {
      return;
    }

    if (
      trackingEventType !== TrackingEventType.Click &&
      trackingEventType !== TrackingEventType.Rotate &&
      trackingEventType !== TrackingEventType.AnimationComplete &&
      trackingEventType !== TrackingEventType.AnimationInterrupted
    ) {
      return;
    }

    trackCdp({
      name: `ThreeSixtyView:${trackingEventType}`,
      params: {
        feature: 'shop',
        order_number: cart.number,
        // Protecting from reserve words - https://zeroheight.com/55c402603/p/069ad1-amplitude/t/42db9c
        product: {
          base_product_key: product.base_product_key,
          product_key: product.product_key,
          ...product.tracking_data,
        },
      },
    });
  };

  const loadImageFromUrl = (url, index) => {
    const image = new Image();

    image.srcset = shopImageSrcSet({
      baseUrl: url,
      type: 'galleryThreeSixty',
      fm: 'jpg',
      q: 90,
    });

    image.src = shopImageUrl({
      baseUrl: url,
      type: 'galleryThreeSixty',
      fm: 'jpg',
      q: 90,
    });

    image.onload = () => {
      setLoadedImages((prev) => {
        const copy = [...prev];
        copy[index] = image;
        return copy;
      });
    };
  };

  useEffect(() => {
    if (!inView) return;
    trackEvent(TrackingEventType.Click);
  }, [inView]);

  useEffect(() => {
    if (!firstImageLoadedButNotAll && !allImagesLoaded) {
      // Load first image for background based on inital rotation
      loadImageFromUrl(
        images[calculatedInitalRotationIndex],
        calculatedInitalRotationIndex,
      );
    }

    if (allImagesLoaded || (lazyLoad && !inView)) {
      return;
    }

    images.forEach((url, i) => {
      // Skip if image is already loaded
      if (loadedImages[i]) return;

      loadImageFromUrl(url, i);
    });
  }, [images, inView, lazyLoad, allImagesLoaded, firstImageLoadedButNotAll]);

  useEffect(() => {
    if (!firstImageLoadedButNotAll && !allImagesLoaded) {
      // show nothing because the first image didnt load yet + all images didnt load it
      return;
    }

    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    const img =
      loadedImages[
        firstImageLoadedButNotAll
          ? calculatedInitalRotationIndex
          : currentImageIdx
      ];

    if (!canvas || !context || !img) return;

    const rect = canvas.parentNode.getBoundingClientRect();
    const dpi = window.devicePixelRatio;
    canvas.width = rect.width * dpi;
    canvas.height = rect.height * dpi;

    const imgRatio = img.width / img.height;
    const canvasRatio = canvas.width / canvas.height;

    let destHeight = canvas.height;
    let destWidth = canvas.width;

    if (imgRatio < canvasRatio) {
      destHeight = destWidth / imgRatio;
    } else {
      destWidth = destHeight * imgRatio;
    }

    const offsetX = (canvas.width - destWidth) * CENTERED_OFFSET_MULTIPLIER;
    const offsetY = (canvas.height - destHeight) * CENTERED_OFFSET_MULTIPLIER;

    // Only wrap drawImage in try-catch for graceful degradation.
    // This is the only canvas operation that can fail due to GPU issues.
    // Primary protection is in ImageGallery via shouldRenderThreeSixtyView().
    try {
      context.drawImage(img, offsetX, offsetY, destWidth, destHeight);
    } catch {
      // Canvas drawing failed due to GPU/context issues - leave canvas blank.
    }
  }, [allImagesLoaded, firstImageLoadedButNotAll, currentImageIdx, inView]);

  const rotate = (delta) => {
    setCurrentImageIdx((prevIdx) => {
      let newIdx = prevIdx + (delta % images.length);

      // Handle out-of-bounds indexes
      if (newIdx < 0) {
        newIdx = images.length + newIdx;
      } else if (newIdx >= images.length) {
        newIdx = newIdx - images.length;
      }

      return newIdx;
    });
  };

  const resetRotateInterval = () => {
    if (!rotateInterval) {
      return;
    }

    clearInterval(rotateInterval);
    rotateInterval = null;
  };

  const animateRotations = (lambda, framesPerSec = fps) => {
    setCurrentImageIdx(calculatedInitalRotationIndex);
    rotateInterval = setInterval(lambda, 1000 / framesPerSec);
  };

  const setAnimationCompleteState = () => {
    resetRotateInterval();
    setIsSliderVisible(true);
    trackEvent(TrackingEventType.AnimationComplete);
  };

  const fullRevolutionAnimation = (maxRevolutions = 1) => {
    setIsSliderVisible(false);
    animateRotations(() => {
      if (rotationCount === images.length * Math.floor(maxRevolutions)) {
        setAnimationCompleteState();
        return;
      }
      rotate(-1);
      rotationCount++;
    });
  };

  const frontViewAnimation = () => {
    setIsSliderVisible(false);
    animateRotations(
      () => {
        if (rotationCount === images.length) {
          setAnimationCompleteState();
          return;
        }

        const delta =
          rotationCount <= Math.floor(images.length * 0.25) ||
          rotationCount > Math.floor(images.length * 0.75)
            ? -1
            : 1;

        rotate(delta);
        rotationCount++;
      },
      Math.floor(fps * 0.8),
    );
  };

  const onDragStart = (event) => {
    if (rotateInterval) {
      trackEvent(TrackingEventType.AnimationInterrupted);
    }

    setDragging(true);
    setLastX(event.pageX);
    resetRotateInterval();
    setIsSliderVisible(false);
    trackEvent(TrackingEventType.Rotate);
  };

  const onDrag = (event) => {
    if (!dragging || !allImagesLoaded) {
      return;
    }

    const offset = event.pageX - lastX;
    const roundingFunc = offset > 0 ? Math.floor : Math.ceil;
    const delta = roundingFunc(offset / movementThreshold);

    if (delta !== 0) {
      rotate(delta);
      setLastX(event.pageX);
    }
  };

  useEffect(() => {
    if (!allImagesLoaded || !inView) {
      resetRotateInterval(rotateInterval);
      setIsSliderVisible(false);
      return;
    }

    // Start animation
    switch (introAnimation) {
      case AnimationTypes.FullRevolution: {
        fullRevolutionAnimation();
        break;
      }
      case AnimationTypes.FrontView: {
        frontViewAnimation();
        break;
      }
      case AnimationTypes.None:
      default: {
        break;
      }
    }

    // eslint-disable-next-line consistent-return
    return () => resetRotateInterval();
  }, [allImagesLoaded, inView]);

  return (
    <Box
      role="img"
      aria-label={title}
      onPointerDown={(event) => onDragStart(event)}
      onPointerMove={(event) => onDrag(event)}
      onPointerUp={() => setDragging(false)}
      display="flex"
      justifyContent="center"
      sx={{
        height: '100%',
        width: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        ...(!inView && { display: 'none' }),
        cursor: inView && allImagesLoaded && (dragging ? 'grabbing' : 'grab'),
        touchAction: 'none',
      }}
    >
      {firstImageLoadedButNotAll || allImagesLoaded ? (
        <>
          <Box
            sx={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              ...(!allImagesLoaded && {
                backgroundColor: 'rgba(242, 242, 242, 0.50)',
              }),
            }}
          >
            {allImagesLoaded ? null : <GalleryViewLoader />}
            <Fade in={isSliderVisible}>
              <Box
                display="flex"
                justifyContent="center"
                data-testid={SLIDER_INTRO_CONTAINER_ID}
                flexDirection="column"
              >
                <MarketPill
                  data-testid={DRAG_TO_SPIN_ID}
                  style={{
                    margin: 'auto',
                    marginTop: '2.5%',
                    backgroundColor: 'black',
                    color: 'white',
                    opacity: '0.75',
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                    paddingLeft: theme.spacing(2),
                    paddingRight: theme.spacing(2),
                  }}
                  size={'medium'}
                >
                  <CircularArrowIcon />
                  &ensp;{t('productPage.threeSixty.sliderText')}
                </MarketPill>
              </Box>
            </Fade>
          </Box>
          <canvas
            data-testid={CANVAS_ID}
            ref={canvasRef}
            style={{
              height: '100%',
              width: '100%',
              backgroundColor: 'red',
            }}
          />
        </>
      ) : (
        <GalleryViewLoader />
      )}
    </Box>
  );
};

export default ThreeSixtyView;
